<template>
  <div class="controlbar" :class="videoSeekClass">
    <div class="controlbar_date">
      <DateTimePicker
        :date="timestampVal"
        :disabled="isPlayback"
        :hide-data="isSeekbarLayoutMode"
        @getDate="getDate($event)"
      />
    </div>
    <div v-if="fullscreenMode" class="controlbar_header-gap" />
    <div
      v-if="!fullscreenMode"
      class="controlbar_header"
      :class="{ 'controlbar_header-small': isSeekbarLayoutMode }"
    >
      <div class="controlbar_btns">
        <v-btn
          v-show="isFullLayoutMode && !isPlayback"
          small
          class="controlbar_btn"
          :elevation="captureMode ? 0 : 1"
          :class="{ 'controlbar_btn-active': captureMode }"
          @click="!captureMode && $emit('toggleCaptureMode', true)"
        >
          <span class="controlbar_btn_icon icon icon-camera2" />スナップショット
        </v-btn>
        <v-btn
          v-show="isFullLayoutMode && !isPlayback"
          small
          class="controlbar_btn"
          :elevation="trimMode ? 0 : 1"
          :class="{ 'controlbar_btn-active': trimMode }"
          @click="!trimMode && $emit('toggleTrimMode', true)"
        >
          <span class="controlbar_btn_icon icon icon-cut" />ビデオクリップ
        </v-btn>
      </div>
      <div class="controlbar_right">
        <div v-show="isFullLayoutMode" class="controlbar_time-range">
          <v-select
            v-model="selectedTimeRange"
            flat
            :items="timeRangeItems"
            :menu-props="{ top: true, offsetY: true }"
            item-text="text"
            item-value="val"
            @change="handleTimeRangeChange"
          />
        </div>
      </div>
    </div>
    <div v-if="isLive" class="controlbar_vod-gap" />
    <div v-show="isFullLayoutMode" class="controlbar_vod">
      <div class="controlbar_vod_speed">
        <v-select
          v-model="playbackRateVal"
          :items="playbackRates"
          item-text="text"
          item-value="val"
          :menu-props="{
            offsetY: true,
            contentClass: 'controlbar_vod_speed--vMenu',
          }"
        />
      </div>
      <div class="controlbar_vod_controls">
        <v-btn
          text
          class="controlbar_vod_btn controlbar_vod_btn-backward"
          @click="addTime(-10, 'seconds', false)"
        >
          <span class="icon icon-backward" />
        </v-btn>
        <v-btn
          text
          class="controlbar_vod_btn controlbar_vod_btn-back-ten-frame"
          @click="addTime(-333, 'milliseconds', true)"
        >
          <back-ten-frame-svg :color="fullscreenMode ? '#FFFFFF' : null" />
        </v-btn>
        <v-btn
          text
          class="controlbar_vod_btn controlbar_vod_btn"
          @click="addTime(-33, 'milliseconds', true)"
        >
          <span class="icon icon-back-frame" />
        </v-btn>
        <v-btn
          text
          class="controlbar_vod_btn controlbar_vod_btn-playing"
          @click="playToggle()"
        >
          <span
            class="icon"
            :class="[isPlaying ? 'icon-pause' : 'icon-play']"
          />
        </v-btn>
        <v-btn
          text
          class="controlbar_vod_btn"
          @click="addTime(33, 'milliseconds', true)"
        >
          <span class="icon icon-next-frame" />
        </v-btn>
        <v-btn
          text
          class="controlbar_vod_btn controlbar_vod_btn-next-ten-frame"
          @click="addTime(333, 'milliseconds', true)"
        >
          <next-ten-frame-svg :color="fullscreenMode ? '#FFFFFF' : null" />
        </v-btn>
        <v-btn
          text
          class="controlbar_vod_btn controlbar_vod_btn-forward"
          @click="addTime(10, 'seconds', false)"
        >
          <span class="icon icon-forward" />
        </v-btn>
      </div>
      <div class="controlbar_vod_liveToggle">
        <v-btn
          v-if="!isPlayback"
          x-small
          rounded
          depressed
          class="controlbar_vod_liveToggle-btn"
          @click="showLive()"
        >
          LIVE<span class="icon icon-play" />
        </v-btn>
      </div>
    </div>
    <span v-if="isSeekbarLayoutMode" class="videoSeek-timegap" />
    <div v-show="!isDatetimeLayoutMode" class="controlbar_indicator_container">
      <div class="controlbar_indicator" />
    </div>
    <div
      class="controlbar_bar"
      :class="{ 'controlbar_hide--all': isDatetimeLayoutMode }"
      @mousedown="mouseover = true"
    >
      <timeline
        ref="timeline"
        :items="items"
        :options="options"
        :events="[
          'rangechange',
          'rangechanged',
          'mouseDown',
          'timechange',
          'timechanged',
          'select',
        ]"
        @dragover="mouseover = true"
        @mousedown.prevent="mouseover = true"
        @rangechange="timechange"
        @select="onEventClick(true)"
      />
      <video-seek-trim v-if="trimMode" @onABChange="handleClipABChange" />
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import DateTimePicker from '@/components/inputs/DateTimePicker.vue';
import VideoSeekTrim from '@/components/VideoSeekTrim.vue';
import BackTenFrameSvg from './icons/BackTenFrameSVG.vue';
import NextTenFrameSvg from './icons/NextTenFrameSVG.vue';

export default {
  name: 'SeekSlider',
  components: {
    DateTimePicker,
    VideoSeekTrim,
    BackTenFrameSvg,
    NextTenFrameSvg,
  },
  props: {
    name: {
      type: String,
      default: '',
    },
    live: {
      type: Boolean,
      default: true,
    },
    fullscreenMode: {
      type: Boolean,
      default: false,
    },
    playing: {
      type: Boolean,
      default: false,
    },
    captureMode: {
      type: Boolean,
      default: false,
    },
    trimMode: {
      type: Boolean,
      default: false,
    },
    playbackRate: {
      type: Number,
      default: 1,
    },
    timestamp: {
      type: Number,
      default: dayjs().valueOf(),
    },
    isPlayback: {
      type: Boolean,
      default: false,
    },
    events: {
      type: Array,
      default: () => [],
    },
    memory: {
      type: Object,
      default: null,
    },
    videoRef: {
      type: Object,
      default: () => {},
    },
    layoutMode: {
      type: String,
      default: 'full',
    },
    timeRange: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      startPoint: 0,
      endPoint: 0,
      middlePoint: 0,
      layoutModeOpen: false,
      isMouseDown: false,
      isMovingTimeline: false,
      isMovingTimelineTimeout: null,
      playbackRates: [
        { val: 0.5, text: '0.5倍速' },
        { val: 1, text: '1倍速' },
        { val: 2, text: '2倍速' },
      ],
      timeRangeItems: [
        { val: 1, text: '1時間' },
        { val: 3, text: '3時間' },
        { val: 6, text: '6時間' },
        { val: 12, text: '12時間' },
        { val: 24, text: '24時間' },
      ],
      layoutModeItems: [
        { text: '全表示', value: 'full' },
        { text: 'シークバーのみ表示', value: 'seekbar' },
        { text: '日時のみ表示', value: 'datetime' },
      ],
      timelineDuration: 1, // Timeline duration in hours (default: 1)
      selectedTimeRange: 1,
      groups: [],
      options: {
        editable: false,
        zoomMin: dayjs.duration({ hours: 1 }).asMilliseconds(), // one day in milliseconds
        zoomMax: dayjs.duration({ days: 1 }).asMilliseconds(), // about three months in milliseconds
        format: {
          minorLabels: {
            second: 'HH:mm',
            minute: 'HH:mm',
          },
        },
        zoomable: false,
        // timeAxis: { scale: 'minute', step: 5 }
      },
      loopMax: null,
      loopNow: null,
      restart: null,
      stop: false,
      timestampVal: 0,
      currentEvent: null,
    };
  },
  computed: {
    range() {
      return (this.timelineDuration * 60) / 2;
    },
    isLive: {
      get() {
        return this.live;
      },
      set(val) {
        this.$emit('update:live', val);
        if (val) {
          this.$emit('setTime', dayjs(new Date()));
        }
      },
    },
    isPlaying: {
      get() {
        return this.playing;
      },
      set(val) {
        this.$emit('update:playing', val);
      },
    },
    playbackRateVal: {
      get() {
        return this.playbackRate;
      },
      set(val) {
        this.$emit('update:playbackRate', val);
      },
    },
    items() {
      const items = [];
      this.events.forEach((event, index) => {
        if (event && event.start && event.end) {
          items.push({
            className: 'controlbar_event',
            end: event.end,
            id: 'event-' + event.eventId + '_' + index,
            start: event.start,
            type: 'background',
          });
        }
      });

      if (this.memory) {
        items.push({
          className: 'controlbar_memory',
          end: this.memory.end,
          id: 'memory',
          start: this.memory.start,
          type: 'background',
        });
      }

      return items;
    },
    isFullLayoutMode() {
      return this.layoutMode === 'full' || this.fullscreenMode;
    },
    isSeekbarLayoutMode() {
      return this.layoutMode === 'seekbar' && !this.fullscreenMode;
    },
    isDatetimeLayoutMode() {
      return this.layoutMode === 'datetime' && !this.fullscreenMode;
    },
    videoSeekClass() {
      return {
        controlbar_fullscreen: this.fullscreenMode,
        'controlbar_display-mode--full': this.isFullLayoutMode,
        'controlbar_display-mode--seekbar': this.isSeekbarLayoutMode,
        'controlbar_display-mode--datetime': this.isDatetimeLayoutMode,
      };
    },
    dateTimeNudgeTop() {
      if (this.isFullLayoutMode) {
        return 60;
      } else if (this.isSeekbarLayoutMode) {
        return 35;
      } else {
        return 35;
      }
    },
    dateTimeNudgeRight() {
      if (this.isDatetimeLayoutMode) {
        return -50;
      } else {
        return -100;
      }
    },
  },
  watch: {
    timelineDuration: {
      handler: function (val) {
        if (val && this.$refs.timeline) {
          // const timeline = this.$refs.timeline.timeline
          // timeline.setOptions(this.options)
        }
      },
      immediate: true,
    },
    timestamp: {
      handler: function (val) {
        if (this.isPlaying && this.$refs.timeline && !this.isMovingTimeline) {
          this.checkEventRange();
          this.handleTimestampChange(val);
        }
      },
      immediate: true,
    },
    timeRange(val) {
      this.syncTimeRange(val);
    },
  },
  created() {
    this.init();
  },
  beforeDestroy() {
    const { timeline } = this.$refs.timeline;
    if (timeline) {
      timeline.off('mousewheel', this.handleMousewheel);
      timeline.off('mouseDown', this.handleMousedown);
      timeline.off('mouseMove', this.handleMousemove);
      timeline.off('release', this.handleRelease);
    }
  },
  mounted() {
    const { timeline } = this.$refs.timeline;
    timeline.on('mousewheel', this.handleMousewheel);
    timeline.on('mouseDown', this.handleMousedown);
    timeline.on('mouseMove', this.handleMousemove);
    timeline.on('release', this.handleRelease);
    this.getMax();
    this.syncTimeRange(this.timeRange);
  },
  unmounted() {
    clearInterval(this.loopMax);
    clearInterval(this.loopNow);
    clearInterval(this.restart);
  },
  methods: {
    syncTimeRange(val) {
      if (val == null) {
        return;
      }
      const hasChanged =
        this.timelineDuration !== val || this.selectedTimeRange !== val;
      if (!hasChanged) {
        return;
      }
      this.selectedTimeRange = val;
      this.timelineDuration = val;
      this.$nextTick(() => {
        const timeline = this.$refs.timeline?.timeline;
        if (timeline) {
          this.handleTimestampChange(this.timestampVal || this.timestamp);
        }
      });
    },
    handleSelectLayoutMode(item) {
      this.$emit('layoutModeChanged', item.value);
    },
    handleMousedown() {
      this.isMouseDown = true;
    },
    handleMousemove() {
      if (this.isMouseDown) {
        this.isMovingTimeline = true;
      }
    },
    handleRelease() {
      this.isMouseDown = false;
      if (this.isMovingTimelineTimeout) {
        clearTimeout(this.isMovingTimelineTimeout);
      }
      this.isMovingTimelineTimeout = setTimeout(() => {
        this.isMovingTimeline = false;
      }, 500);
    },
    refresh(timestamp) {
      clearInterval(this.restart);
      let playPoint = dayjs(timestamp);
      const start = playPoint
        .subtract(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      const end = playPoint
        .add(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      this.options.start = start;
      this.options.end = end;

      this.options.max = end;
      const livePlay = () => {
        this.$emit('setTime', dayjs(start).add(this.range, 'minutes'));
        clearInterval(this.restart);
      };
      this.restart = setInterval(livePlay, 500);
      this.timestampVal = dayjs(start).add(this.range, 'minutes').valueOf();
    },
    handleClipABChange(ab) {
      const { timeline } = this.$refs.timeline;
      const startPixelInSecond =
        (ab.start * this.timelineDuration * 60 * 60) / ab.parentWidth;
      const endPixelInSecond =
        (ab.end * this.timelineDuration * 60 * 60) / ab.parentWidth;
      const start = dayjs(timeline.range.start)
        .add(startPixelInSecond, 'seconds')
        .valueOf();
      const end = dayjs(timeline.range.start)
        .add(endPixelInSecond, 'seconds')
        .valueOf();
      this.$emit('onTrimChange', { start, end, movePoint: ab.movePoint });
    },
    init() {
      let playPoint = dayjs(this.timestamp);
      const start = playPoint
        .subtract(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      const end = playPoint
        .add(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      this.options.start = start;
      this.options.end = end;

      this.options.max = end;
    },
    handleTimeRangeChange(val) {
      this.$emit('onSetTimeRange', val);
      this.selectedTimeRange = val;
      this.timelineDuration = val;
      this.handleTimestampChange(this.timestampVal);
    },
    handleMousewheel(e) {
      e.preventDefault();
      e.stopPropagation();
      let delta = 0;
      if (event.wheelDelta) {
        /* IE/Opera. */
        delta = event.wheelDelta / 120;
      } else if (event.detail) {
        /* Mozilla case. */
        // In Mozilla, sign of delta is different than in IE.
        // Also, delta is multiple of 3.
        delta = -event.detail / 3;
      } else if (event.deltaY) {
        delta = -event.deltaY / 3;
      }
      this.timelineDuration -= delta;
      this.timelineDuration =
        this.timelineDuration < 1
          ? Math.max(1, this.timelineDuration)
          : this.timelineDuration > 24
          ? 24
          : this.timelineDuration;

      this.selectedTimeRange = this.getRangeByHour(this.timelineDuration);
      this.handleTimestampChange(this.timestampVal);
    },
    handleTimestampChange(val) {
      const timeline = this.$refs.timeline.timeline;
      if (!timeline) {
        return;
      }
      this.timestampVal = val;
      const day = dayjs(val);
      const start = day
        .subtract(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      let end = day.add(this.range, 'minutes').format('YYYY-MM-DDTHH:mm:ss');

      let max = dayjs()
        .add(this.range, 'minutes')
        .format('YYYY-MM-DDTHH:mm:ss');
      timeline.setOptions({ start });
      timeline.setOptions({ end, max });
    },
    updateTime(timestamp) {
      this.$emit('updateTime', { timestamp });
    },
    handleRangeChanged({ start, end }) {
      const startDate = dayjs(start);
      const endDate = dayjs(end);
      const duration = dayjs.duration(endDate.diff(startDate)).asHours();

      this.timelineDuration = duration;
      this.selectedTimeRange = this.getRangeByHour(duration);
    },
    getRangeByHour(hour) {
      if (hour < 3) {
        return 1;
      } else if (hour < 6) {
        return 3;
      } else if (hour < 12) {
        return 6;
      } else if (hour < 24) {
        return 12;
      } else {
        return 24;
      }
    },
    getMax() {
      const timeline = this.$refs.timeline.timeline;

      this.loopMax = setInterval(() => {
        if (timeline) {
          try {
            // const max = dayjs().add(this.range, 'minutes').format('YYYY-MM-DDTHH:mm:ss')
            // timeline.setOptions({ max }) // TODO: Set max
          } catch (error) {
            // Do nothing
          }
        }
      }, 1000);
    },
    playToggle() {
      this.stop = false;
      this.isPlaying = !this.isPlaying;
    },
    showLive() {
      this.isLive = true;
    },
    checkEventRange() {
      if (this.events.length) {
        const currentEvent = this.events.find(
          (event) =>
            +this.timestampVal + 1 >= dayjs(event.start).valueOf() &&
            +this.timestampVal <= dayjs(event.end).valueOf()
        );
        this.currentEvent = currentEvent;
        this.$emit('onEvent', currentEvent);
      }
    },
    timechange(e) {
      const { middleUnix, memoryStartDay, memoryEndDay, middleDay } =
        this.getStartMiddleEnd(e);
      this.startPoint = memoryStartDay.valueOf();
      this.endPoint = memoryEndDay.valueOf();
      this.middlePoint = middleUnix;
      if (this.stop) {
        this.isPlaying = false;
        return;
      }
      if (this.isMouseDown) {
        if (e) {
          this.handleRangeChanged(e);
        }
        this.isLive = false;

        if (this.memory && this.memory.start && this.memory.end) {
          this.onMemory({
            middleUnix,
            memoryStartDay,
            memoryEndDay,
            middleDay,
          });
        }
      }
    },
    getDate(val) {
      this.isAuto = false;
      this.isPlaying = false;
      this.isLive = false;
      this.timestampVal = val;
      const day = dayjs(val);
      this.$emit('setTime', day);
    },
    addTime(val, unit, pauseAfterLoad) {
      if (pauseAfterLoad) {
        this.isAuto = false;
        this.isPlaying = false;
      }
      let newTimestamp = this.timestampVal;
      if (val > 0) {
        newTimestamp = dayjs(this.timestampVal).add(val, unit).valueOf();
      } else {
        newTimestamp = dayjs(this.timestampVal)
          .subtract(val * -1, unit)
          .valueOf();
      }

      const memoryStartUnix = dayjs(this.memory.start).valueOf();
      const memoryEndUnix = dayjs(this.memory.end).valueOf();

      if (newTimestamp > memoryEndUnix) {
        newTimestamp = memoryEndUnix;
      } else if (newTimestamp < memoryStartUnix) {
        newTimestamp = memoryStartUnix;
      }
      this.timestampVal = newTimestamp;
      this.handleTimestampChange(this.timestampVal);
      this.$emit('setTime', dayjs(this.timestampVal));
    },
    onEventClick(play = false) {
      const start = dayjs(this.items[0].start);
      this.isAuto = false;
      if (play) {
        this.stop = false;
      }
      this.$emit('setTime', start);
    },
    changeCurrentTime(payload) {
      this.$emit('changeCurrentTime', payload);
    },
    getStartMiddleEnd({ start, end }) {
      const startDay = dayjs(start);
      const endDay = dayjs(end);
      const diffDay = endDay.diff(startDay);
      const middleDay = startDay.add(diffDay / 2, 'ms');
      const middleUnix = middleDay.valueOf();
      const memoryStartDay = dayjs(this.memory.start);
      const memoryEndDay = dayjs(this.memory.end);
      return {
        middleUnix,
        memoryStartDay,
        memoryEndDay,
        middleDay,
      };
    },
    onMemory({ middleUnix, memoryStartDay, memoryEndDay, middleDay }) {
      if (middleUnix < memoryStartDay.valueOf()) {
        this.timestampVal = memoryStartDay.valueOf();
        this.$emit('setTime', memoryStartDay);
      } else if (middleUnix > memoryEndDay.valueOf()) {
        this.timestampVal = memoryEndDay.valueOf();
        this.$emit('setTime', memoryEndDay);

        this.isPlaying = false;
      } else {
        this.timestampVal = middleUnix;
        this.$emit('setTime', middleDay);
      }
    },
  },
};
</script>

<style lang="scss">
.controlbar {
  position: relative;
  &-timegap {
    height: 3px;
    display: block;
  }
  &_display-mode--datetime {
    .dateTimePicker {
      &_dateTime {
        display: flex;
        margin-top: 10px;
      }
      &_select {
        font-size: 16px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #0075c2;
        margin-top: 0;
        line-height: 2;
        margin-right: 26px;
      }
      &_time {
        font-size: 16px;
        font-weight: bold;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #0075c2;
        line-height: 2;
      }
    }
    .controlbar {
      &_date {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
      &_right {
        margin-top: 11px;
      }
    }
    &.controlbar {
      max-height: 46px;
    }
  }
  &_right {
    display: flex;
    align-items: center;
  }
  &_date {
    display: flex;
    justify-content: center;
    font-size: 18px;
    line-height: 32px;
    font-weight: 600;
    position: absolute;
    width: 100%;
    text-align: center;
    top: 0;
    color: var(--primary-color);
    @media (max-width: 1199px) {
      top: 32px;
    }
    &-fullscreen {
      padding-right: 40px;
    }
  }
  &_hide {
    visibility: hidden;
    max-height: 0;
    max-width: 0;
    min-height: 0 !important;
    &--all {
      &,
      & * {
        visibility: hidden;
        max-height: 0;
        max-width: 0;
        min-height: 0 !important;
      }
    }
  }
  &_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    &-small {
      margin-bottom: -12px;
    }
    @media (max-width: 1199px) {
      margin-bottom: 60px;
    }
  }
  &_header-gap {
    margin-bottom: 28px;
    height: 28px;
    @media (max-width: 1199px) {
      margin-bottom: 60px;
    }
  }
  &_btn {
    margin-right: 10px;

    &_icon {
      margin-right: 6px;
      font-size: 13.3px;
    }
    &-active {
      box-shadow: 0 1px 2px 0 rgb(0 0 0 / 16%) !important;
      background-color: #dbe2f3 !important;
    }
  }
  &_menu {
    border-radius: 10px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.12);
    background-color: #fff;
    width: 182px;
    height: 152px;
    &-icon {
      &.v-btn.v-size--default {
        font-size: 15px;
      }
    }

    &-header {
      padding: 13px 16px 0;
      h4 {
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #666;
      }
      margin-bottom: 7px;
    }
    &-title-active {
      color: #0075c2 !important;
      font-weight: bold !important;
    }
    &-list {
      &-active {
        color: rebeccapurple;
        .v-list-item__icon {
          .icon.icon-check {
            color: #0075c2;
            font-size: 19px;
          }
        }
      }
      &.v-list {
        padding: 0;

        .v-list-item {
          max-height: 32px;
          min-height: 32px;
        }
        .v-list-item__title {
          font-size: 14px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          letter-spacing: normal;
          text-align: left;
          color: #666;
          display: flex;
          align-items: center;
        }
        .v-list-item__icon:first-child {
          margin-right: 0;
        }
        .v-list-item__icon {
          margin: 5px 0;
        }
      }
    }
  }
  &_time {
    font-size: 16px;
    line-height: 24px;
    height: 24px;
    font-weight: 600;
    width: 100%;
    text-align: center;
    color: inherit;
  }
  &_time-range {
    width: 120px;
    .v-text-field {
      padding-top: 0;
      margin-top: 0;
      .v-input__append-inner {
        padding-left: 0;
      }
    }
    .v-input__slot {
      margin-bottom: 0;
    }
    .v-text-field__details {
      display: none;
    }
    .v-select__selection {
      flex-grow: 1;
      text-align: right;
      margin-right: 0;
      max-width: 100%;
    }
    .v-text-field input {
      width: 0;
      flex-grow: 0;
    }
    .v-select__selection.v-select__selection--comma {
      font-size: 12px;
      font-weight: 600;
      color: #989898;
    }
    .v-text-field > .v-input__control > .v-input__slot:before {
      content: none;
    }
    .v-text-field > .v-input__control > .v-input__slot:before {
      border-style: none;
    }
    .v-text-field > .v-input__control > .v-input__slot:after {
      border-style: none;
    }
  }
  &_vod {
    display: flex;
    justify-content: center;
    align-items: center;
    &_controls {
      width: 350px;
      display: flex;
      justify-content: space-around;
      align-items: center;
      margin: 0 15px;
    }
    &-gap {
      height: 35px;
    }
    &_speed {
      width: 120px;
      .v-text-field {
        padding-top: 0;
        margin-top: 0;
        .v-input__append-inner {
          padding-left: 0;
        }
      }
      .v-input__slot {
        margin-bottom: 0;
      }
      .v-text-field__details {
        display: none;
      }
      .v-select__selection {
        flex-grow: 1;
        text-align: right;
        margin-right: 0;
        max-width: 100%;
      }
      .v-text-field input {
        width: 0;
        flex-grow: 0;
      }
      .v-select__selection.v-select__selection--comma {
        font-size: 12px;
        font-weight: 600;
        color: #989898;
      }
      .v-text-field > .v-input__control > .v-input__slot:before {
        content: none;
      }
      &--vMenu {
        margin-top: -30px;
        border-radius: 10px;
        .v-list-item__title {
          gap: 10px;
          display: flex;
          padding-left: 30px !important;
        }
        .v-list-item--active {
          .v-list-item__title {
            color: #0075c2 !important;
            padding-left: 0 !important;
          }
        }
      }
    }
    &_btn {
      &.v-btn:not(.v-btn--round).v-size--default {
        padding: 0;
        min-width: 35px;
        margin: 0 5px;
      }
      .icon {
        color: #989898;
        font-size: 26px;
      }
    }
    &_liveToggle {
      height: 21px;
      width: 120px;
      display: flex;
      justify-content: center;
      &-btn {
        &.theme--light.v-btn.v-btn--has-bg {
          background-color: #dbe2f3;
          height: 21px;
          padding: 0 6px;
        }
        .v-btn__content {
          font-size: 14px;
          font-weight: 600;
          line-height: 21px;
        }
      }
    }
  }
  &_indicator {
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 8px 6.5px 0 6.5px;
    border-color: #999 transparent transparent transparent;
    &_container {
      display: flex;
      justify-content: center;
      margin-top: 5px;
    }
  }
  &_bar {
    border-radius: 5px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
    min-height: 41px;
    position: relative;
  }

  &_event {
    &.vis-item.vis-background {
      background-color: var(--primary-color);
      cursor: pointer;
    }
  }

  &_memory {
    &.vis-item.vis-background {
      background-color: #c9effc;
      cursor: pointer;
    }
  }

  .vis-timeline {
    border: 0;
    height: 41px !important;
  }
  .vis-panel {
    height: 19px !important;
  }
  .vis-current-time {
    background-color: #f3f3f3 !important;
    width: 100% !important;
    height: 18px !important;
  }
  .vis-time-axis.vis-background {
    height: 12px !important;
    top: 7px !important;
    z-index: 3 !important;
    pointer-events: none;
  }
  .vis-time-axis .vis-grid.vis-minor,
  .vis-time-axis .vis-grid.vis-major {
    height: 12px !important;
    border-left: 1px solid #999;
    border-right: 1px solid #999;
    top: -18px;
    background-image: linear-gradient(
      90deg,
      transparent 0%,
      transparent calc(20% - 1px),
      #999 calc(20% - 1px),
      #999 20%,
      transparent 20%,
      transparent calc(40% - 1px),
      #999 calc(40% - 1px),
      #999 40%,
      transparent 40%,
      transparent calc(60% - 1px),
      #999 calc(60% - 1px),
      #999 60%,
      transparent 60%,
      transparent calc(80% - 1px),
      #999 calc(80% - 1px),
      #999 80%,
      transparent 80%
    );
    background-size: 100% 8px;
    background-position: bottom;
  }
  .vis-text.vis-minor {
    left: -18px;
    font-size: 11px;
    font-weight: normal;
    color: #999;
  }
  .vis-time-axis.vis-foreground {
    height: 22px;
  }
  .vis-text.vis-major {
    display: none;
  }
  .vis-panel {
    &.vis-center {
      height: 19px !important;
      border-top: 0;
      top: 0px !important;
      pointer-events: all;
      z-index: 1;
    }
    &.vis-top {
      background-color: #f3f3f3;
    }
  }
  .vis-shadow {
    display: none;
  }
  .vis-item.vis-range {
    top: 0 !important;
    border-width: 0;
    border-radius: 0;
    height: 18px;
    min-width: 15px;
  }
  .vis-panel > .vis-content {
    transform: translateY(0) !important;
  }
  .vis-itemset {
    height: 18px !important;
  }
  .vis-group {
    height: 19px !important;
  }
  .vis-panel.vis-bottom {
    top: 19px !important;
  }
  &_fullscreen {
    .controlbar {
      &_date {
        padding-right: 40px;
        color: #fff;
      }
      &_vod {
        color: #fff;
        &_btn {
          color: inherit !important;
          .v-btn__content {
            color: inherit;
            .icon {
              color: inherit;
            }
          }
        }
        &_speed {
          color: inherit;
          .v-select {
            color: inherit;
            .v-input__slot {
              color: inherit;
              .v-select__selections {
                color: inherit;
                .v-select__selection--comma {
                  color: inherit;
                }
              }
              .v-input__append-inner {
                .theme--light.v-icon {
                  color: inherit;
                }
              }
            }
          }
        }
        &_liveToggle {
          &-btn {
            background-color: transparent !important;
            border: solid 1px #fff;
            color: #fff !important;
          }
        }
      }
      &_bar {
        box-shadow: none;
      }
      &_indicator {
        border-color: #fff transparent transparent transparent;
      }
    }
    // timeline
    .vis-panel {
      &.vis-center {
        border-top: 1px solid #bfbfbf !important;
        border-right: 1px solid #bfbfbf !important;
        border-left: 1px solid #bfbfbf !important;
      }
      &.vis-top {
        background-color: #8a9da4;
      }
    }
    .vis-text.vis-minor {
      color: #fff;
    }
    .vis-time-axis .vis-grid.vis-minor,
    .vis-time-axis .vis-grid.vis-major {
      border-left: 1px solid #fff;
      border-right: 1px solid #fff;
      background-image: linear-gradient(
        90deg,
        transparent 0%,
        transparent calc(20% - 1px),
        #fff calc(20% - 1px),
        #fff 20%,
        transparent 20%,
        transparent calc(40% - 1px),
        #fff calc(40% - 1px),
        #fff 40%,
        transparent 40%,
        transparent calc(60% - 1px),
        #fff calc(60% - 1px),
        #fff 60%,
        transparent 60%,
        transparent calc(80% - 1px),
        #fff calc(80% - 1px),
        #fff 80%,
        transparent 80%
      );
    }
    .vis-current-time {
      background-color: #4a4a4a !important;
      width: 100% !important;
      height: 18px !important;
    }
    .vis-itemset .vis-background,
    .vis-itemset .vis-foreground {
      border: 1px solid #bfbfbf !important;
      border-radius: 5px;
    }
  }
}
</style>
